import { isNil } from 'lodash-es'
import IntlMessageFormat from 'intl-messageformat'
import '@formatjs/intl-pluralrules/polyfill'
import '@formatjs/intl-pluralrules/locale-data/en'
import moment from 'moment'
import { DateFormat } from 'api/generated'
import { DateLike, Decimal } from 'utils'
import { Translation } from './Translation'
import translations from './translations/en-GB'

// locale is hard-coded to en-GB for now
const currentLocale = 'en-GB'
const currentTranslations = translations

/* Caching */

const messageFmtCache: Record<string, IntlMessageFormat> = {}
const numberFmtCache: Record<string, Intl.NumberFormat> = {}

function getMessageFormatter(message: string): IntlMessageFormat {
  const cacheKey = `${currentLocale}-${message}`

  if (!messageFmtCache[cacheKey]) {
    messageFmtCache[cacheKey] = new IntlMessageFormat(message, currentLocale)
  }
  return messageFmtCache[cacheKey]
}

function getNumberFormatter(options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const cacheKey = `${currentLocale}-${JSON.stringify(options)}`

  if (!numberFmtCache[cacheKey]) {
    numberFmtCache[cacheKey] = new Intl.NumberFormat(currentLocale, options)
  }
  return numberFmtCache[cacheKey]
}

/* Formatting */

export function translate(translation: Translation, args?: any) {
  const message = currentTranslations[translation]
  if (isNil(message)) {
    return translation
  } else {
    const formatter = getMessageFormatter(message)
    const result = formatter.format(args)

    if (typeof result === 'string') {
      return result
    }

    // format method of IntlMessageFormat type has changed, and it can be void or
    // return arrays, our translate method is not prepared for it, we should
    // keep using it just to translate regular strings, not html blocks or React code
    throw new Error('Unexpected use of translate')
  }
}

export interface NumberFormatOptions {
  maxDp?: number
  minDp?: number
  minDigits?: number
  percent?: boolean
  basisPoints?: boolean
  separator?: boolean
  sign?: boolean
  parenOnNegative?: boolean
  decimalShift?: number
  showDecimalShiftSuffix?: boolean
  flipSign?: boolean
  absoluteIncrease?: boolean
  absoluteDecrease?: boolean
  maxFractionDigits?: number
}

export function formatNumber(num: number | null | undefined, options: NumberFormatOptions = {}): string {
  if (isNil(num)) {
    return ''
  } else {
    const numberFormat = getNumberFormatter({
      minimumIntegerDigits: options.minDigits,
      minimumFractionDigits: options.minDp,
      maximumFractionDigits: options.maxFractionDigits ?? 8,
      useGrouping: options.separator !== false
    })

    if (options.decimalShift) {
      num = Decimal.shift(num, options.decimalShift)
    }

    if (options.flipSign) {
      num = Decimal.multiply(num, -1)
    } else if (options.absoluteIncrease) {
      num = Decimal.add(100, num)
    } else if (options.absoluteDecrease) {
      num = Decimal.subtract(100, num)
    }

    let result = numberFormat.format(num)

    if (options.showDecimalShiftSuffix && options.decimalShift) {
      if (options.decimalShift === -3) {
        result = `${result}K`
      } else if (options.decimalShift === -6) {
        result = `${result}M`
      }
    }

    if (options.percent) {
      result = `${result}%`
    } else if (options.basisPoints) {
      result = `${result}bp`
    }

    if (options.sign && num > 0) {
      result = `+${result}`
    }

    if (!options.sign && options.parenOnNegative && num < 0) {
      result = `(${result.slice(1)})`
    }

    return result
  }
}

export interface BooleanFormatOptions {
  defaultValue?: boolean
}

export function formatBoolean(value: boolean | null | undefined, options: BooleanFormatOptions = {}): string {
  const displayedValue = isNil(value) ? options.defaultValue : value

  if (isNil(displayedValue)) {
    return ''
  } else if (displayedValue) {
    return translate(Translation.TC_TRUE)
  } else {
    return translate(Translation.TC_FALSE)
  }
}

const getDateFormatString = (dateFormat: DateFormat) => {
  switch (dateFormat) {
    case DateFormat.EU:
      return 'DD/MM/YYYY'
    case DateFormat.US:
      return 'MM/DD/YYYY'
    case DateFormat.DD_MMM_YYYY:
      return 'DD MMM YYYY'
    default:
      throw new Error(`Unhandled DateFormat ${[dateFormat]}`)
  }
}

export function formatTimestamp(value: DateLike | null, dateFormat: DateFormat) {
  return isNil(value) ? '' : moment(value).format(`${getDateFormatString(dateFormat)} HH:mm:ss`)
}

export function formatTime(value: DateLike | null) {
  return isNil(value) ? '' : moment(value).format('HH:mm:ss')
}

export function formatDate(value: DateLike | null, dateFormat: DateFormat) {
  return isNil(value) ? '' : moment(value).format(getDateFormatString(dateFormat))
}

export function formatAsUTCDate(value: DateLike | null, dateFormat: DateFormat) {
  return isNil(value) ? '' : moment.utc(value).format(getDateFormatString(dateFormat))
}

export function formatDateAndTime(value: DateLike | null, dateFormat: DateFormat) {
  const formattedDate = isNil(value) ? '' : moment(value).format(`${getDateFormatString(dateFormat)} HH:mm:ss`)
  return formattedDate === 'Invalid date' ? 'N/A' : formattedDate
}
